import { Injectable, inject } from '@angular/core';
import { PromptDialogData, PromptDialogService } from '@components/prompt-dialog';
import { HttpApiResponse, HttpApiResponseState } from '@core/http';
import { IDType } from '@core/models/types';
import { ILocationChangedEvent, LocationService, NavigationService } from '@core/services';
import { FeatureTypes } from '@core/user';
import { BaseMixinService } from '@mixins/services';
import { CoreServiceMixin } from '@mixins/services/core';
import { TranslatableServiceMixin } from '@mixins/services/translatable';
import { isNullishArray } from '@utils/array';
import { isNullish } from '@utils/object';
import { isNullishString, uuid } from '@utils/string';
import { ToastMessageService, ToasterType } from '@widgets/toast-message';
import { DUPLICATE_DIALOG_TRAD_KEY, WIZARD_UI_TRAD_KEY } from '@wizard/constants';
import { WizardBasketBroadcastEvent, WizardBasketBroadcastEventEnum, WizardBasketBroadcastEventListener } from '@wizard/events';
import { WizardBasketEvent } from '@wizard/events/wizard-basket.event';
import { basketsAreDifferent, createWizardBasket } from '@wizard/helpers';
import { WizardBasket, WizardBasketData, WizardBasketEntityKey, WizardBasketList, WizardBasketMeta } from '@wizard/models';
import { BasicWizardBasketStatusType, WizardBasketAction, WizardBasketActions } from '@wizard/types';
import { normalizeColumnType } from '@wizard/wizard-panel/helpers';
import { BehaviorSubject, Observable, Subject, catchError, filter, firstValueFrom, map, of, shareReplay, take } from 'rxjs';
import { WizardApiService } from './wizard-api.service';

export const WIZARD_BASKET_CHANNEL = 'WIZARD_BASKET_CHANNEL';
export const WIZARD_BASKET_LS = 'WizardBasket';

interface IUpdateBasketOptions {
  silent: boolean;
  broadcastUpdate: boolean;
  type: WizardBasketAction;
}

@Injectable({ providedIn: 'root' })
export class WizardBasketController extends TranslatableServiceMixin(CoreServiceMixin(BaseMixinService)) {

  /** @Inject */
  readonly #locationService = inject(LocationService);
  readonly #navigationService = inject(NavigationService);
  readonly #toastService = inject(ToastMessageService);
  readonly #promptDialogService = inject(PromptDialogService);
  readonly #wizardApiService = inject(WizardApiService);

  readonly #basketEvent = new Subject<WizardBasketEvent>();
  // 🔥 hot observable
  readonly #basketEventObs = this.#basketEvent.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #basket = new BehaviorSubject<WizardBasket>(null);
  // 🔥 hot observable
  readonly #basketObs = this.#basket.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #basketList = new BehaviorSubject<WizardBasketList>(null);
  // 🔥 hot observable
  readonly #basketListObs = this.#basketList.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #basketHasBeenChanged = new BehaviorSubject(false);
  // 🔥 hot observable
  readonly #basketHasBeenChangedObs = this.#basketHasBeenChanged.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #onSavingBasket = new BehaviorSubject(false);
  // 🔥 hot observable
  readonly #onSavingBasketObs = this.#onSavingBasket.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #isUserBasket = new BehaviorSubject(false);

  // belong to isUserBasket AND contexte
  readonly #isEditable = new BehaviorSubject(false);
  // 🔥 hot observable
  readonly #isEditableObs = this.#isEditable.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  // ⚠️ should be equal to (NOT isUserBasket) -> but they're independants
  readonly #isLocked = new BehaviorSubject(true);
  // 🔥 hot observable
  readonly #isLockedObs = this.#isLocked.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  /* BroadcastChannel */
  // TODO 🧰 make a controller for this feature
  readonly #wizardBasketChannel = new BroadcastChannel(WIZARD_BASKET_CHANNEL);
  readonly #wizardBasketChannelID = uuid();
  #updatedBasketFromBroadcastChannel: WizardBasket = null;
  #updatedKeyBasketFromBroadcastChannel: string = null;
  readonly basketHasBeenUpdatedViaBroadcast$ = new BehaviorSubject(false);

  readonly #wizardResultsRoute = this.getAppConfig('wizardResultsRoute');
  readonly #wizardBasketsRoute = this.getAppConfig('wizardBasketsRoute');
  readonly #businessCatalogOverviewRoute = this.getAppConfig('businessCatalogOverviewRoute');

  #lastUpdateType: WizardBasketAction;

  constructor() {
    super();
    this.injected.userController.hasUser$
      .pipe(filter(hasUser => !!hasUser), take(1))
      .subscribe(() => this.initialise());
  }

  get basket$(): Observable<WizardBasket> { return this.#basketObs; }
  get basket(): WizardBasket { return this.#basket.getValue(); }
  // TODO 🧰 security ?
  set basket(basket: WizardBasket) { this.updateBasket(basket, { type: WizardBasketActions.Update }); }

  get basketList$(): Observable<WizardBasketList> { return this.#basketListObs; }
  get basketList(): WizardBasketList { return this.#basketList.getValue(); }

  get basketHasBeenChanged$(): Observable<boolean> { return this.#basketHasBeenChangedObs; }
  get basketHasBeenChanged(): boolean { return this.#basketHasBeenChanged.getValue(); }

  get isSavingBasket$(): Observable<boolean> { return this.#onSavingBasketObs; }
  get savingBasket(): boolean { return this.#onSavingBasket.getValue(); }

  get isUserBasket$(): Observable<boolean> { return this.#isUserBasket.asObservable(); }
  get isUserBasket(): boolean { return this.#isUserBasket.getValue(); }

  get isEditable$(): Observable<boolean> { return this.#isEditableObs; }
  get editable(): boolean { return this.#isEditable.getValue(); }

  get isLocked$(): Observable<boolean> { return this.#isLockedObs; }
  get locked(): boolean { return this.#isLocked.getValue(); }

  get updatedBasketFromBroadcastChannel(): WizardBasket { return this.#updatedBasketFromBroadcastChannel; }
  get updatedKeyBasketFromBroadcastChannel(): string { return this.#updatedKeyBasketFromBroadcastChannel; }

  get userHasWizardFeature(): boolean { return this.injected.userController.hasFeature(FeatureTypes.ReferentialCompanyWizard); }
  get userId(): IDType { return this.user?.id; }

  // ❄️ cold observable
  get isBasketEmpty$(): Observable<boolean> { return this.#basket.asObservable().pipe(map(basket => isBasketEmpty(basket))); }
  get isBasketEmpty(): boolean { return isBasketEmpty(this.#basket.getValue()); }
  // ❄️ cold observable
  get hasBasket$(): Observable<boolean> { return this.#basket.asObservable().pipe(map(basket => !!basket)); }
  get hasBasket(): boolean { return !!this.#basket.getValue(); }

  get basketReferential(): IDType { return this.#basket.getValue()?.referentialId; }
  get basketVersion(): IDType { return this.#basket.getValue()?.versionId; }

  get basketReferential$(): Observable<IDType> { return this.#basketObs.pipe(map(basket => basket?.referentialId)); }
  get basketVersion$(): Observable<IDType> { return this.#basketObs.pipe(map(basket => basket?.versionId)); }

  basketChanges(): Observable<WizardBasketEvent> {
    return this.#basketEventObs;
  }

  displayWizardBasketResults(): boolean {
    if (this.hasBasket && !this.doesLocationContains(this.#wizardResultsRoute)) {
      this.navigateToWizardResults();
      return true;
    } else {
      return false;
    }
  }

  async copyRecipeToSpecification(recipe: string): Promise<WizardBasket> {
    const merged = { ops: [] };
    const specObject = isNullishString(this.basket?.specification) ? null : JSON.parse(this.basket?.specification);

    if (!specObject || isNullishArray(specObject.ops)) {
      merged.ops = JSON.parse(recipe).ops;
    } else {
      merged.ops = specObject.ops.concat(JSON.parse(recipe).ops);
    }

    return this.updateBasketSpecification(JSON.stringify(merged));
  }

  async updateBasketSpecification(specification: string): Promise<WizardBasket> {
    const currentBasket = this.#basket.getValue();

    if (currentBasket && specification !== currentBasket.specification) {
      return await this.updateBasket({
        ...currentBasket,
        specification
      }, { type: WizardBasketActions.Update });
    }

    return new Promise((resolve) => resolve(currentBasket));
  }

  async refreshBasketFromBroadcastChannel(): Promise<WizardBasket> {
    return new Promise((resolve) => {
      const newBasket = this.#updatedBasketFromBroadcastChannel;

      // 🔥 important
      this.validateBasketRights(newBasket);
      this.#basket.next(newBasket);
      this.#basketHasBeenChanged.next(true);
      this.fireEvent(WizardBasketActions.New);

      return resolve(newBasket);
    });
  }

  async createNewBasket(): Promise<WizardBasket> {
    return this.createNewWizardBasket();
  }

  async createNewCopy(copyName?: string): Promise<WizardBasket> {
    const currentCopy = this.#basket.getValue();
    const name = copyName || `${currentCopy.name} - ${this.translate('Common.ACopy')}`;

    return this.createWizardBasket(name)
      .then(newBasket => this.updateBasket(
        {
          ...newBasket,
          data: structuredClone(currentCopy.data), name
        },
        {
          type: WizardBasketActions.New,
          broadcastUpdate: true,
        }
      ));
  }

  async loadBasket(basket: WizardBasket): Promise<WizardBasket> {
    return firstValueFrom(this.#wizardApiService.getBasket(basket)).then(this.onLoadBasket.bind(this));
  }

  async saveBasket(basket?: WizardBasket): Promise<WizardBasket> {
    let basketToSave = basket
      ? structuredClone(basket)
      : structuredClone(this.#basket.getValue());
    const isUserBasket = this.checkIfUserBasket(basketToSave);
    const normalizedItems = {};

    Object.entries(basketToSave.data)?.map(([key, data]) => {
      if (data) {
        normalizedItems[key] = data.map(item => {
          let itemCopy = structuredClone(item);
          if (itemCopy.items) {
            itemCopy.items = item.items?.map(itm => {
              return {
                ...itm,
                type: normalizeColumnType(itm),
              };
            });
          }
          return itemCopy;
        });
      } else {
        normalizedItems[key] = null;
      }
    });

    basketToSave.data = {
      ...structuredClone(basketToSave.data),
      ...structuredClone(normalizedItems),
    };

    if (isUserBasket) {
      return this.finalizeSaveBasket(basketToSave);
    } else {
      return firstValueFrom(
        this.#promptDialogService.open({
          data: {
            title: this.translate(`${DUPLICATE_DIALOG_TRAD_KEY}.Title`),
            messages: [
              this.translate(`${DUPLICATE_DIALOG_TRAD_KEY}.Message.1`),
              this.translate(`${DUPLICATE_DIALOG_TRAD_KEY}.Message.2`),
            ],
            type: 'primary',
            icon: 'content_copy',
            fields: [
              {
                name: `duplicateName`,
                value: `${basketToSave.name} - ${this.translate('Common.ACopy')}`,
                mandatory: true
              }
            ]
          } as PromptDialogData
        })
          .afterClosed()
      ).then(data => {
        if (data && !isNullishArray(data.fields) && data.fields[0]) {
          return this.createWizardBasket(data.fields[0].value as string)
            .then(createdBasketToSave => this.finalizeSaveBasket({
              ...createdBasketToSave,
              specification: basketToSave.specification,
              data: structuredClone(basketToSave.data)
            }));
        } else {
          return this.#basket.getValue();
        }
      });
    }
  }

  async finalizeSaveBasket(basket: WizardBasket): Promise<WizardBasket> {
    const basketToSave = structuredClone(basket);

    this.#onSavingBasket.next(true);

    if (isNullishString(basketToSave.creationDate)) { basketToSave.creationDate = new Date().toISOString(); }
    basketToSave.updateDate = new Date().toISOString();

    return firstValueFrom<HttpApiResponse<WizardBasket>>(
      this.#wizardApiService.saveBasket(basketToSave).pipe(catchError(this.onHttpError.bind(this)))
    ).then(this.onBasketSaved.bind(this));
  }

  async updateBasket(basket: WizardBasket, options: Partial<IUpdateBasketOptions> = { type: WizardBasketActions.Update }): Promise<WizardBasket> {
    const { silent, broadcastUpdate, type } = options;

    return this.checkIfAnyBasketCreated()
      .then(currentBasket => {
        const whichKeyIsDifferent = basketsAreDifferent(basket, currentBasket);

        /* 🧰 keep */
        console.log(
          '🔥 updateBasket ',
          '\n',
          '[type: ', type, this.#lastUpdateType,
          ' | silent: ', !!silent,
          ' | broadcast: ', !!broadcastUpdate,
          ' | diff: ', whichKeyIsDifferent, ']',
          '\n',
          basket
        );

        // 🔥 performance, do shallow copy ?
        const currentBasketCopy = structuredClone(currentBasket);
        const updatedBasketCopy = structuredClone(basket);
        const data = structuredClone(updatedBasketCopy.data);
        const updatedBasket = {
          ...currentBasketCopy,
          ...updatedBasketCopy,
          data
        };
        let finalType: WizardBasketAction;

        /* 🔥 important */
        this.validateBasketRights(updatedBasket);
        this.#basket.next(updatedBasket);
        this.#basketHasBeenChanged.next(true);

        if (this.#lastUpdateType === WizardBasketActions.Loaded && type === WizardBasketActions.MetaChanged) {
          this.#lastUpdateType = type;
          finalType = WizardBasketActions.Loaded;
        } else if (!this.#lastUpdateType && type === WizardBasketActions.MetaChanged) {
          this.#lastUpdateType = type;
          finalType = WizardBasketActions.DisplayResults;
        } else {
          this.#lastUpdateType = type;
          finalType = type;
        }

        this.fireEvent(this.isBasketEmpty ? 'Clear' : finalType, { whichKeyIsDifferent });

        if (broadcastUpdate) {
          this.#wizardBasketChannel.postMessage({
            key: WIZARD_BASKET_LS,
            id: this.#wizardBasketChannelID,
            user: this.user,
            data: updatedBasket,
            action: type,
            timeStamp: Date.now()
          } as WizardBasketBroadcastEvent);
        }

        return updatedBasket;
      });
  }

  async deleteBasketFromServer(basket?: WizardBasket): Promise<boolean> {
    const basketToDelete = basket
      ? structuredClone(basket)
      : structuredClone(this.basket);

    return firstValueFrom(
      this.#wizardApiService.deleteBasket(basketToDelete)
    )
      .then(response => {
        const deleted = response.state === HttpApiResponseState.Success;

        if (deleted) {
          this.displayToast(this.translate(`${WIZARD_UI_TRAD_KEY}.Toaster.DeleteBasketMessage`), 'Success');

          if (!basket) {
            this.deleteBasket();
          } else {
            const basketList = this.#basketList.getValue();
            const index = basketList.findIndex((basketItem => basketItem.id === basket.id));

            if (index > -1) { basketList.splice(index, 1); }

            this.#basketList.next(basketList);
          }
        } else {
          // TODO do something
          this.displayToastError();
        }
        return deleted;
      });
  };

  validateDisplayResults(action: WizardBasketAction = 'Update') {
    this.fireEvent(action);
  }

  async updateBasketData(data: WizardBasketData, silent = false): Promise<WizardBasket> {
    return this.checkIfAnyBasketCreated().then(basket => this.updateBasket(
      { ...basket, data },
      {
        type: WizardBasketActions.Update,
        silent, broadcastUpdate: true
      }
    ));
  }

  async updateBasketMeta(basketMeta: Partial<WizardBasketMeta>, silent = false): Promise<WizardBasket> {
    if ('referentialId' in basketMeta) {
      const currentReferentialId = this.#basket.getValue().referentialId;
      const newReferentialId = basketMeta.referentialId;

      if (currentReferentialId !== newReferentialId) {
        const versionId = this.injected.referentialsController.getReferentialById(newReferentialId)?.versions[0].id;
        basketMeta.versionId = versionId;
      }

      basketMeta.bwMigration = Boolean(basketMeta.bwMigration);
    }

    return this.checkIfAnyBasketCreated().then(basket => {
      return this.updateBasket(
        { ...basket, ...basketMeta },
        {
          silent,
          type: WizardBasketActions.MetaChanged,
          broadcastUpdate: true,
        }
      );
    });
  };

  updateBasketStatus(basket: WizardBasket, newStatus: BasicWizardBasketStatusType) {
    this.#wizardApiService.updateBasketStatus(basket, newStatus)
      .subscribe(response => this.onBasketUpdated(response, basket, newStatus));
  }

  clearBasket(broadcastUpdate = true) {
    const clearedBasket = {
      ...this.#basket.getValue(),
      bwMigration: false,
      data: null
    };
    this.#basketHasBeenChanged.next(false);
    this.#basket.next(clearedBasket);

    if (broadcastUpdate) {
      this.#wizardBasketChannel.postMessage({
        id: this.#wizardBasketChannelID,
        key: WIZARD_BASKET_LS,
        user: this.user,
        data: clearedBasket,
        action: 'Clear',
        timeStamp: Date.now()
      } as WizardBasketBroadcastEvent);
    }

    this.fireEvent('Clear');
  }

  deleteBasket(broadcastUpdate = true) {
    this.#basketHasBeenChanged.next(false);
    this.#basket.next(null);

    if (broadcastUpdate) {
      this.#wizardBasketChannel.postMessage({
        id: this.#wizardBasketChannelID,
        key: WIZARD_BASKET_LS,
        user: this.user,
        data: null,
        action: 'Delete',
        timeStamp: Date.now()
      } as WizardBasketBroadcastEvent);
    }

    this.fireEvent('Delete');
  }

  getBasketList() {
    this.#wizardApiService.getBasketList().subscribe(this.onBasketListLoad.bind(this));
  }

  validateBasketRights(basket: WizardBasket): { isUserBasket: boolean; isEditable: boolean; isLocked: boolean; } {
    const isUserBasket = this.checkIfUserBasket(basket);
    const isEditable = this.checkIfBasketCanBeModified(basket);
    const isLocked = this.checkIfBasketIsLocked(basket);

    // 🔥 important - order 1
    this.#isUserBasket.next(isUserBasket);
    // 🔥 important - order 2
    this.#isEditable.next(isEditable);
    // 🔥 important - order 3
    this.#isLocked.next(isLocked);

    return { isUserBasket, isEditable, isLocked };
  }

  navigateToWizardResults() {
    this.#navigationService.navigate(
      [`./${this.#wizardResultsRoute}`],
      this.versionId()
        ? { queryParams: { version: this.versionId() } }
        : {}
    );
  }

  navigateToWizardBaskets() {
    this.#navigationService.navigate(
      [`./${this.#wizardBasketsRoute}`],
      this.versionId()
        ? { queryParams: { version: this.versionId() } }
        : {}
    );
  }

  navigateToBuzinessCatalog() {
    if (this.doesLocationContains(`${this.#wizardResultsRoute}`)) {
      this.#navigationService.navigate([this.#businessCatalogOverviewRoute],
        this.versionId()
          ? { queryParams: { version: this.versionId() } }
          : {}
      );
    }
  }

  private initialise() {
    this.initialiseBroadcast();

    this.#locationService.locationChanged().subscribe(this.onLocationChanged.bind(this));

    // TODO 🪛 this subscribtion is only here to "start" basketChanges emit something, why, i do not know
    // TODO check this ;)
    this.basketChanges().subscribe();

    /* get basket from other tabs */
    this.#wizardBasketChannel.postMessage({
      id: this.#wizardBasketChannelID,
      key: WIZARD_BASKET_LS,
      user: this.user,
      data: null,
      action: 'Get',
      timeStamp: Date.now()
    } as WizardBasketBroadcastEvent);
  }

  private initialiseBroadcast() {
    /* broadcast channel */
    // TODO 💥 make an Controller for this
    this.#wizardBasketChannel.addEventListener('message', (event: WizardBasketBroadcastEventListener) => {
      /* 🧰 keep consoles until alls good */
      console.log('🔥 CHANNEL ', event, this.userId, this.#wizardBasketChannelID, this.#basket.getValue());
      const eventData = event.data;
      const eventAction = eventData.action;
      const eventUser = eventData.user;
      const isEventFromUser = eventUser.id === this.userId;
      // 🔥 only same user share same data
      if (!isEventFromUser) { return; }
      const eventId = eventData.id;
      // ✅ self broadcast, skip it
      if (eventId === this.#wizardBasketChannelID) {
        return;
      }

      const currentBasket = this.#basket.getValue();
      const updatedBasket = eventData.data;

      if (eventAction === WizardBasketBroadcastEventEnum.Get) {
        this.#wizardBasketChannel.postMessage({
          id: this.#wizardBasketChannelID,
          key: WIZARD_BASKET_LS,
          user: this.user,
          data: currentBasket,
          action: 'Post',
          timeStamp: Date.now()
        } as WizardBasketBroadcastEvent);
      } else if ( // Update and Post are equal, Post always update the basket
        eventAction === WizardBasketBroadcastEventEnum.Update
        ||
        eventAction === WizardBasketBroadcastEventEnum.Post
      ) {
        if (eventAction === WizardBasketBroadcastEventEnum.Post && !currentBasket && !updatedBasket) {
          return;
        }
        const isUpdateAction = eventAction === WizardBasketBroadcastEventEnum.Update;
        const currentLocation = this.#locationService.pathName;
        const isUpdateLockedLocation = currentLocation.includes(this.getAppConfig('wizardResultsRoute'));
        const whichKeyIsDifferent = basketsAreDifferent(updatedBasket, currentBasket);
        // 🔥 force true if action is Post
        const different = (!currentBasket || !isUpdateAction) ? true : !!whichKeyIsDifferent;

        this.#updatedBasketFromBroadcastChannel = { ...updatedBasket };
        this.#updatedKeyBasketFromBroadcastChannel = whichKeyIsDifferent as string;

        if (isUpdateLockedLocation && !!whichKeyIsDifferent) {
          if (whichKeyIsDifferent) {
            if ((whichKeyIsDifferent as WizardBasketEntityKey) === 'name') {
              this.updateBasket({ ...updatedBasket }, { type: WizardBasketActions.MetaChanged });
            } else {
              this.fireEvent('UpdateFromOtherTab');
            }
          }
        } else if (different) {
          this.updateBasket({ ...updatedBasket }, { type: WizardBasketActions.Update });
          this.fireEvent('UpdateFromOtherTab');
        }
      } else if (eventAction === WizardBasketBroadcastEventEnum.Clear) {
        this.clearBasket(false);
      } else if (eventAction === WizardBasketBroadcastEventEnum.Delete) {
        this.deleteBasket(false);
      }
    });
  }

  private onBasketListLoad(response: HttpApiResponse<WizardBasketList>) {
    if (response.state === HttpApiResponseState.Success) {
      const basketList = isNullishArray(response.result)
        ? []
        : response.result.sort((a, b) => new Date(b.updateDate ?? b.creationDate).getTime() - new Date(a.updateDate ?? a.creationDate).getTime());
      this.#basketList.next(basketList);
    } else {
      this.#basketList.next([]);
    }
  }

  private onBasketSaved(response: HttpApiResponse<WizardBasket>) {
    if (response.state === HttpApiResponseState.Success) {
      this.updateBasket(response.result)
        .then(() => this.displayToast(this.translate(`${WIZARD_UI_TRAD_KEY}.Toaster.SaveBasketMessage`), 'Success'));
    } else {
      // 🪛 TODO do something ?
      this.displayToastError();
    }

    this.#onSavingBasket.next(false);

    return response;
  }

  private onBasketUpdated(response: HttpApiResponse<boolean>, basket: WizardBasket, newStatus: BasicWizardBasketStatusType) {
    if (response.state === HttpApiResponseState.Success) {
      const basketList = this.#basketList.getValue()?.map(basketItem => {
        const isTargetBasket = basketItem.id === basket.id;

        return {
          ...basketItem,
          updateDate: isTargetBasket ? new Date().toISOString() : basketItem.updateDate,
          status: isTargetBasket ? newStatus : basketItem.status
        };
      });

      this.#basketList.next(basketList);
      this.displayToast(this.translate(`${WIZARD_UI_TRAD_KEY}.Toaster.UpdateBasketStatusMessage`), 'Success');
    } else {
      // 🪛 TODO do something ?
      this.displayToastError();
    }
  }

  private async createNewWizardBasket(): Promise<WizardBasket> {
    const newBasket = await this.createWizardBasket();
    return await this.updateBasket(newBasket, { broadcastUpdate: true, type: WizardBasketActions.New });
  }

  private async createWizardBasket(name?: string): Promise<WizardBasket> {
    return new Promise(resolve => {
      const newBasket = createWizardBasket(
        name || this.translate('Wizard.Data.NewName'),
        this.user,
        {
          referentialId: this.referential?.id,
          versionId: this.version?.id
        }
      );

      this.#basket.next(newBasket);

      return resolve(newBasket);
    });
  }

  private doesLocationContains(path: string): boolean {
    return this.#locationService.pathName.includes(path);
  }

  private versionId(): IDType {
    // ❌ TODO use the global controller value ?
    return +this.#locationService.getParams('version');
  }

  private fireEvent(action: WizardBasketAction, meta = {}) {
    this.#basketEvent.next({
      timeStamp: performance.now(),
      basket: this.basket,
      action,
      meta,
    });
  }

  private async checkIfAnyBasketCreated(): Promise<WizardBasket> {
    return (!this.#basket.getValue())
      ? this.createWizardBasket()
      : new Promise(resolve => resolve(this.#basket.getValue()));
  }

  private checkIfUserBasket(basket: WizardBasket): boolean {
    if (!basket) { return false; }
    return this.userId === basket.user?.id;
  }

  // ⚠️ this is an important validation for UI there - 🧰 make a controller
  private checkIfBasketCanBeModified(basket: WizardBasket): boolean {
    if (!basket) { return false; }
    // TODO 🧰 add a const "ban" list
    // basket can not be edited in '/wizard-results'
    return !this.doesLocationContains(this.#wizardResultsRoute);
  }

  // ⚠️ this is an important validation for UI there - 🧰 make a controller
  private checkIfBasketIsLocked(basket: WizardBasket): boolean {
    if (!basket) { return false; }
    // TODO 🧰 add a const "ban" list
    // basket is locked in '/wizard-results'
    return this.doesLocationContains(this.#wizardResultsRoute);
  }

  private async onLoadBasket(response: HttpApiResponse<WizardBasket>): Promise<WizardBasket> {
    this.deleteBasket(false);

    if (response.state === HttpApiResponseState.Success) {
      this.#basketHasBeenChanged.next(false);
      this.#lastUpdateType = WizardBasketActions.Loaded;
      return this.updateBasket(response.result, { broadcastUpdate: true, type: WizardBasketActions.Loaded });
    } else {
      // TODO do something here
      return new Promise((resolve) => resolve(null));
    }
  }

  private onLocationChanged(event: ILocationChangedEvent) {
    if (
      event.previous === `/${this.#wizardResultsRoute}`
      &&
      (event.current !== `/${this.#businessCatalogOverviewRoute}`)
    ) {
      this.deleteBasket(false);
    }

    this.validateBasketRights(this.#basket.getValue());
  }

  private onHttpError(error: any): Observable<any> {
    return of(error);
  }

  private displayToast(message: string, type: ToasterType) {
    this.#toastService.show(message, type);
  }

  private displayToastError() {
    this.displayToast(this.translate('Http.Error.Default'), 'Warn');
  }
}

const isBasketEmpty = (basket: WizardBasket): boolean => {
  if (isNullish(basket)) { return true; }
  const basketData = basket.data;
  if (isNullish(basketData)) { return true; }
  return Object.values(basketData).every(d => isNullishArray(d));
};
